import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
  Select,
  type SelectOptionProps,
} from "@/lib/components/ui/Select/Select";

import styles from "./LanguageSwitcher.module.scss";

const languages: SelectOptionProps<string>[] = [
  { value: "en", label: "English" },
  { value: "de", label: "Deutsch" },
  { value: "fr", label: "Français" },
  { value: "it", label: "Italiano" },
];

const LANGUAGE_PREFERENCE_KEY = "preferredLanguage";

const normalizeLanguageCode = (code: string): string =>
  code.toLowerCase().replace(/[-_]/g, "");

const toLanguageAbbreviation = (code: string): string =>
  normalizeLanguageCode(code).slice(0, 2).toUpperCase();

export const LanguageSwitcher = (): JSX.Element => {
  const { i18n } = useTranslation();

  const currentLang = i18n.language;
  const normalizedCurrentLang = normalizeLanguageCode(currentLang);

  const selectedLanguage =
    languages.find(
      (lang) => normalizeLanguageCode(lang.value) === normalizedCurrentLang,
    )?.value ??
    languages.find((lang) =>
      normalizedCurrentLang.startsWith(normalizeLanguageCode(lang.value)),
    )?.value;

  useEffect(() => {
    const savedLanguage = localStorage.getItem(LANGUAGE_PREFERENCE_KEY);

    if (!savedLanguage) {
      return;
    }

    const normalizedSavedLanguage = normalizeLanguageCode(savedLanguage);
    const matchedLanguage =
      languages.find(
        (lang) => normalizeLanguageCode(lang.value) === normalizedSavedLanguage,
      )?.value ??
      languages.find((lang) =>
        normalizedSavedLanguage.startsWith(normalizeLanguageCode(lang.value)),
      )?.value;

    if (
      matchedLanguage &&
      normalizeLanguageCode(i18n.language) !==
        normalizeLanguageCode(matchedLanguage)
    ) {
      void i18n.changeLanguage(matchedLanguage);
    }
  }, [i18n]);

  const handleSelect = (code: string) => {
    localStorage.setItem(LANGUAGE_PREFERENCE_KEY, code);
    void i18n.changeLanguage(code);
  };

  return (
    <div className={styles.language_switcher}>
      <Select
        options={languages}
        value={selectedLanguage}
        onChange={handleSelect}
        popoverAlign="end"
        emptyLabel={toLanguageAbbreviation(currentLang)}
        triggerLabelFormatter={(selectedOptions) =>
          toLanguageAbbreviation(selectedOptions[0]?.value ?? currentLang)
        }
      />
    </div>
  );
};
